export const STARTING_ELO = 1000;

export const MAX_FIXED_PARTNER_TEAMS = 6;

export const STORAGE_KEY_LIVE_TOURNAMENT = "ccb-live-tournament-state-v1";

export const buildLiveStorageKey = (eventId: string) => `ccb-live-tournament-event-${eventId}`;
export const buildLiveTsKey = (storageKey: string) => `${storageKey}-ts`;

/**
 * ELO estimates keyed by startingLevel string.
 * Tier midpoints match the backend refresh_facts() mapping exactly.
 * Low/High sub-tiers are spread ±25 from each tier midpoint.
 */
const STARTING_LEVEL_ELO: Record<string, number> = {
  Platinum:      1225,
  "High Gold":   1150,
  "Mid Gold":    1125,
  "Low Gold":    1100,
  "High Silver": 1055,
  "Mid Silver":  1030,
  "Low Silver":  1005,
  "High Bronze":  975,
  "Mid Bronze":   950,
  "Low Bronze":   925,
  Beginner:       850,
};

export function estimateEloFromLevel(startingLevel: string | null | undefined): number {
  if (!startingLevel) return STARTING_ELO;
  return STARTING_LEVEL_ELO[startingLevel] ?? STARTING_ELO;
}

/**
 * Resolve effective ELO for seeding: actual currentElo if available,
 * otherwise estimate from startingLevel, otherwise STARTING_ELO.
 */
export function getEffectiveElo(
  profile: { currentElo?: number | null; startingLevel?: string | null } | null | undefined
): number {
  if (profile?.currentElo != null && Number.isFinite(profile.currentElo)) {
    return profile.currentElo;
  }
  return estimateEloFromLevel(profile?.startingLevel);
}

/** True when a profile has a meaningful ELO signal (actual or level-derived). */
export function hasEffectiveElo(
  profile: { currentElo?: number | null; startingLevel?: string | null } | null | undefined
): boolean {
  return (
    (profile?.currentElo != null && Number.isFinite(profile.currentElo)) ||
    (profile?.startingLevel != null && profile.startingLevel !== "Unspecified")
  );
}

/** True when ELO is estimated from startingLevel (not a measured value). */
export function isEloEstimated(
  profile: { currentElo?: number | null; startingLevel?: string | null } | null | undefined
): boolean {
  return (
    (profile?.currentElo == null || !Number.isFinite(profile.currentElo as number)) &&
    profile?.startingLevel != null &&
    profile.startingLevel !== "Unspecified"
  );
}

/** Resolve ELO display info for a single player. Returns null when no ELO signal exists. */
export function playerEloInfo(
  profile: { currentElo?: number | null; startingLevel?: string | null } | null | undefined
): { elo: number; isEstimated: boolean } | null {
  if (!hasEffectiveElo(profile)) return null;
  return { elo: getEffectiveElo(profile), isEstimated: isEloEstimated(profile) };
}

/** Number of courts for a player count: floor(playerCount / 4), minimum 1. */
export function effectiveCourts(playerCount: number): number {
  return Math.max(1, Math.floor(playerCount / 4));
}
